import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { RecipeScalerClient } from './ui/RecipeScalerClient';

interface RecipeScalerProps {
    setActiveTool?: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const RecipeScaler: React.FC<RecipeScalerProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Recipe Scaler & Converter"
            description="Easily scale any baking recipe up or down with our free recipe scaler. Our recipe multiplier calculator instantly changes ingredient quantities for any batch size."
            setActiveTool={setActiveTool || (() => { })}
            breadcrumbs={breadcrumbs}
            toolId="recipe-scaler"
        >
            <RecipeScalerClient />
            <RecipeScalerContent />
        </ToolContainer>
    );
};

const RecipeScalerContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Master Your Bakes with Our Free Recipe Resizer</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Baking is a science, and precision is key. Whether you're a home baker needing to <strong>double a recipe</strong> for a party or a professional looking to scale down a commercial formula, our <strong>Recipe Resizer</strong> is your ultimate tool. 
                Running a high-volume bakery? <a href="/saas" className="text-brand-pink font-bold hover:underline">Manage production schedules with Bakery Pro</a>.
            </p>
        </div>

        <div className="bg-gradient-to-br from-brand-pink/5 to-white p-6 rounded-2xl border border-brand-pink/10 shadow-sm">
            <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Recommended Gear</h3>
            <div className="grid sm:grid-cols-2 gap-4">
                <a href="https://amzn.to/3PqXy8C" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl border border-brand-border hover:border-brand-pink transition-all group">
                    <span className="text-3xl">⚖️</span>
                    <div>
                        <p className="font-bold text-brand-text-title">Digital Kitchen Scale</p>
                        <p className="text-xs text-brand-text-body/70">Essential for weighing ingredients accurately.</p>
                    </div>
                </a>
            </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Scale Correctly
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Ingredients:</strong> Enter original weights/quantities.</li>
                    <li><strong>Multiplier:</strong> Set your target batch size (e.g., 0.5 or 2).</li>
                    <li><strong>Download:</strong> Use the PDF button for a clean printout.</li>
                </ol>
            </div>
        </div>
    </div>
);
